import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import axios from "axios";
import { formatDayMonthYear } from "@/lib/dates";
import { GRACE_DAYS, purgeDate } from "@/lib/deletionGrace";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import AddIcon from "@mui/icons-material/Add";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import { useShallow } from "zustand/react/shallow";
import { Dialog } from "@/components/main/utils/Dialog";
import { DialogActions } from "@/components/main/utils/DialogActions";
import { Button } from "components/common/Button";
import { LoadingState } from "components/common/LoadingState";
import { Tooltip } from "@/components/common/Tooltip";
import { Callout } from "components/common/Callout";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import { TooltipButton } from "components/common/TooltipButton";
import { SectionRule } from "components/common/SectionRule";
import { Badge } from "components/common/Badge";
import { Stack } from "components/common/Stack";
import { ImpactChips, ImpactRow, plural } from "components/common/ImpactRow";
import { TextInput } from "components/common/TextInput";
import { EditableName } from "components/canvas/utils/EditableName";
import { nameLength } from "@/components/main/utils/naming";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { resolveDatasetColor } from "@/lib/datasetColor";
import {
  grantingSpace,
  grantLabel,
  spaceAncestors,
  spaceColor,
  useOrgContextStore,
} from "@/components/main/stores/orgContextStore";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import {
  cancelSpaceDeletion,
  deleteSpace,
  getSpaceDeletionAnalysis,
  type SpaceDeletionAnalysis,
  fetchOrgMembers,
  fetchSpaceGrants,
  grantSpaceRole,
  isAbandoned,
  revokeSpaceRole,
  sealSpace,
  unsealSpace,
  updateSpace,
  type OrgMember,
  type SpaceGrant,
} from "@/lib/api/organizations";
import { AssignManagerDialog } from "./AssignManagerDialog";
import { SpaceColorPicker } from "./SpaceColorPicker";
import { SpaceDot } from "./SpaceDot";
import { OrgMark } from "@/components/main/organizations/OrgMark";
import { NewSpaceOverlay } from "./NewSpaceOverlay";
import { MAX_SPACE_NAME_CHARS, MIN_SPACE_NAME_CHARS } from "./spaceName";
import { type EmailChip } from "components/shareSection/EmailChipInput";
import { ShareComposer } from "components/shareSection/EmailSection";
import {
  AccessItem,
  PeopleScroll,
  SpaceBlock,
} from "components/shareSection/AccessItem";
import {
  RoleOption,
  RowMenu,
  StaticRole,
} from "components/shareSection/PermissionControl";
import { GovernanceAuditList } from "@/components/main/governance/GovernanceAuditList";
import { GovernanceAuditPanel } from "@/components/main/governance/GovernanceAuditPanel";
import { useGovernanceAudit } from "@/components/main/governance/useGovernanceAudit";
import { CATALOG, MANAGE_ACCESS_MENU_LABEL } from "@/actions/catalog";
import {
  DangerLink,
  SectionCount,
  SettingsFrame,
  useSettingsSection,
  type SettingsSection,
} from "@/components/main/utils/settings/SettingsFrame";

type Section = "general" | "access" | "governance";

const SPACE_ROLES: RoleOption[] = [
  { label: "Manager", note: "owns models" },
  { label: "Collaborator" },
  { label: "Viewer" },
];

/// Rows in the inline preview. The panel shows the full log, paged.
const AUDIT_PREVIEW_ROWS = 5;

const roleLabel = (role: string) =>
  role.charAt(0).toUpperCase() + role.slice(1);

const doorMessage = (error: unknown, fallback: string) =>
  axios.isAxiosError(error) && typeof error.response?.data === "string"
    ? error.response.data
    : fallback;

/// Space settings: name and colour, access (grants, roles, revoke, D26 guests),
/// sealed or inheriting, the governance strip and delete.
export const SpaceSettingsPage: React.FC = () => {
  const { space_id } = useParams();
  const navigate = useNavigate();
  const { spaces, spacesLoading, refreshSpaces, activeOrg, setSelectedSpace } =
    useOrgContextStore(
      useShallow((s) => ({
        spaces: s.spaces,
        spacesLoading: s.spacesLoading,
        refreshSpaces: s.refreshSpaces,
        activeOrg: s.activeOrg,
        setSelectedSpace: s.setSelectedSpace,
      })),
    );
  const space = spaces.find((s) => s.space_id === space_id);
  const parent = spaces.find((s) => s.space_id === space?.parent_space_id);
  const granting =
    space && grantingSpace(spaceAncestors(spaces, space.space_id));
  const org = activeOrg();

  // The caller's view only, so it can under-count: hiding the seal is a courtesy
  // and the server decides. Delete reads the server's pre-flight instead (D50).
  const models = useMainStateStore((s) => s.availableModels);
  const visibleModelCount = useMemo(
    () => models.filter((m) => m.space_id === space_id).length,
    [models, space_id],
  );
  const childSpaceCount = useMemo(
    () => spaces.filter((s) => s.parent_space_id === space_id).length,
    [spaces, space_id],
  );
  const looksEmpty = visibleModelCount === 0 && childSpaceCount === 0;

  const [grants, setGrants] = useState<SpaceGrant[]>([]);
  const [inheritedGrants, setInheritedGrants] = useState<SpaceGrant[]>([]);
  const [auditPanelOpen, setAuditPanelOpen] = useState(false);
  const [revoking, setRevoking] = useState<SpaceGrant | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [grantChips, setGrantChips] = useState<EmailChip[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [grantError, setGrantError] = useState<string | null>(null);
  const [grantRole, setGrantRole] = useState<string>("collaborator");
  const [isGranting, setIsGranting] = useState(false);
  const [granted, setGranted] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createInsideOpen, setCreateInsideOpen] = useState(false);
  // D50: the dialog renders the server's pre-flight, and the delete carries
  // its token — the confirm is a contract about WHAT goes, not a dialog over
  // whatever this client could see.
  const [analysis, setAnalysis] = useState<SpaceDeletionAnalysis | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [typedName, setTypedName] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [sealOpen, setSealOpen] = useState(false);
  const [unsealOpen, setUnsealOpen] = useState(false);

  const selfId = useMemo(() => localStorage.getItem("clientID") ?? "", []);
  // Which grantees govern this space through the ORG rather than through the
  // grant beside their name: an admin's "Collaborator" row is not the whole
  // story, and nothing here used to say so.
  const orgRoleByEmail = useMemo(
    () =>
      new Map(
        members
          .filter((m) => m.role === "owner" || m.role === "org_admin")
          .map((m) => [m.email, m.role]),
      ),
    [members],
  );

  const guestCount = useMemo(
    () => grants.filter((g) => g.is_org_member === false).length,
    [grants],
  );

  // Who the delete actually costs. The presser is excluded — they know.
  const losingAccess = useMemo(
    () =>
      grants
        .filter((g) => g.subject !== `user:${selfId}`)
        .map((g) => ({
          key: `${g.subject}-${g.role}`,
          label: grantLabel(g),
          role: g.role,
        })),
    [grants, selfId],
  );

  const reloadGrants = useCallback(() => {
    if (!space_id) return;
    void fetchSpaceGrants(space_id).then(setGrants);
  }, [space_id]);

  // D19: a non-personal org's spaces reach its members and nobody else, so the
  // grant field offers the roster and refuses anything outside it. A personal
  // org is one member, so it keeps free entry for its D26 guests.
  const orgId = org && !org.is_personal ? org.org_id : null;
  useEffect(() => {
    if (!orgId) {
      setMembers([]);
      return;
    }
    let stale = false;
    void fetchOrgMembers(orgId).then((rows) => {
      if (!stale) setMembers(rows);
    });
    return () => {
      stale = true;
    };
  }, [orgId]);

  // Invited-but-not-yet-joined holds no FGA member tuple until they accept
  // (D43), so the grant door would refuse them.
  const joinedMembers = useMemo(
    () => members.filter((m) => !m.pending && m.email),
    [members],
  );
  const directory = useMemo(
    () =>
      orgId
        ? joinedMembers.map((m) => ({
            email: m.email,
            displayName: m.display_name,
          }))
        : undefined,
    [orgId, joinedMembers],
  );
  const clientIdByEmail = useMemo(
    () =>
      new Map(joinedMembers.map((m) => [m.email.toLowerCase(), m.client_id])),
    [joinedMembers],
  );
  const grantedEmails = useMemo(
    () => grants.map((g) => g.email).filter((e): e is string => !!e),
    [grants],
  );

  useEffect(() => {
    reloadGrants();
  }, [reloadGrants]);

  // Inherited section: the granting space's grants, when the caller can read
  // them; fetchSpaceGrants resolves [] on a refusal, which hides the section.
  const grantingId = granting?.space_id;
  useEffect(() => {
    if (!grantingId) {
      setInheritedGrants([]);
      return;
    }
    void fetchSpaceGrants(grantingId).then(setInheritedGrants);
  }, [grantingId]);

  // 403 (not an org admin) hides Governance; a personal org has no admin
  // distinct from its owner, so the D3 strip would describe you to yourself.
  // Scoped to THIS space — the org's own log lives on the org settings page.
  const audit = useGovernanceAudit(
    org && !org.is_personal ? org.org_id : null,
    space_id,
    AUDIT_PREVIEW_ROWS,
  );

  // One affordance gate for every act here: `can_manage_access` and `can_delete` are
  // both `manager or org_governor`. Not a boundary; the door's refusal decides.
  const governsSpace =
    org?.is_personal === true ||
    org?.role === "owner" ||
    org?.role === "org_admin" ||
    [...grants, ...inheritedGrants].some(
      (g) => g.subject === `user:${selfId}` && g.role === "manager",
    );

  const sections: SettingsSection<Section>[] = [
    { id: "general", label: "General" },
    {
      id: "access",
      label: "Access",
      adornment:
        governsSpace && space && isAbandoned(space) ? (
          <Tooltip title="No manager. Nobody owns the models inside until someone is appointed.">
            <PersonOffOutlinedIcon
              className="text-warning"
              sx={{ fontSize: 12 }}
            />
          </Tooltip>
        ) : (
          <SectionCount count={grants.length + inheritedGrants.length} />
        ),
    },
    ...(audit.available === true
      ? [{ id: "governance" as const, label: "Governance" }]
      : []),
  ];
  // Governance joins the menu only once the audit answers, so a reload on it waits.
  const [section, setSection] = useSettingsSection<Section>(
    sections,
    !!space && audit.available !== null,
  );

  if (!space) {
    return spacesLoading || spaces.length === 0 ? (
      <LoadingState label="Loading space…" />
    ) : (
      <div className="flex h-full flex-col items-center justify-center gap-2">
        <span className="text-fg-regular">This space does not exist here.</span>
        <Button variant="default" onClick={() => navigate("/overview")}>
          Back to overview
        </Button>
      </div>
    );
  }

  /// A crumb goes to what it names: the org's Overview, or a space selected there.
  const goTo = (spaceId: string | null) => () => {
    setSelectedSpace(spaceId);
    navigate("/overview");
  };

  // `EditableName.commit` runs synchronously and reads the return value, so
  // the PUT is fired off and its result handled later.
  const commitName = (name: string): boolean => {
    const trimmed = name.trim();
    if (trimmed === space.name) return true;
    if (nameLength(trimmed) < MIN_SPACE_NAME_CHARS) {
      showNotificationGlobal(
        `Space name must be at least ${MIN_SPACE_NAME_CHARS} characters`,
        "error",
      );
      return false;
    }
    void updateSpace(space.space_id, { name: trimmed }).then((updated) => {
      if (updated) void refreshSpaces();
      else showNotificationGlobal("Rename failed", "error");
    });
    return true;
  };

  const pickColor = async (color: string) => {
    const updated = await updateSpace(space.space_id, { color });
    if (updated) await refreshSpaces();
    else showNotificationGlobal("Color change failed", "error");
  };

  const submitGrant = async () => {
    const emails = grantChips
      .filter((chip) => chip.status === "valid")
      .map((chip) => chip.email);
    if (emails.length === 0 || isGranting) return;
    setGrantError(null);
    setIsGranting(true);

    // A roster address grants by client_id, which skips the door's email
    // lookup; a guest (personal org only) has none, so it goes by address.
    const refused: string[] = [];
    let lastError: unknown = null;
    for (const email of emails) {
      const clientId = clientIdByEmail.get(email.toLowerCase());
      try {
        await grantSpaceRole(
          space.space_id,
          clientId ? { user: clientId } : { email },
          grantRole,
        );
      } catch (error) {
        refused.push(email);
        lastError = error;
      }
    }

    setIsGranting(false);
    setGrantChips((now) =>
      now.filter(
        (chip) => !emails.includes(chip.email) || refused.includes(chip.email),
      ),
    );
    reloadGrants();
    if (refused.length > 0) {
      setGrantError(
        `${doorMessage(lastError, "Share failed")}. Not shared with: ${refused.join(", ")}`,
      );
      return;
    }
    setGranted(true);
    setTimeout(() => setGranted(false), 3000);
  };

  const changeRole = async (grant: SpaceGrant, role: string) => {
    const user = grant.subject.replace(/^user:/, "");
    try {
      await grantSpaceRole(space.space_id, { user }, role, grant.role);
      reloadGrants();
      showNotificationGlobal(
        `${grantLabel(grant)} is now ${roleLabel(role)}`,
        "success",
      );
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Role change failed"), "error");
      reloadGrants();
    }
  };

  // Stranding a space is recoverable — `can_manage_access` composes from the
  // ORG, so a governor can always reappoint — but it is the whole point of this
  // click rather than a side effect, so it is confirmed rather than refused.
  const strandsTheSpace = (grant: SpaceGrant) =>
    grant.role === "manager" &&
    grants.filter((g) => g.role === "manager").length === 1 &&
    !looksEmpty;

  // D66: the door refuses this. A parent's manager — you included — still
  // manages this space after the revoke.
  const isYourOnlyManagerGrant = (grant: SpaceGrant) =>
    grant.subject === `user:${selfId}` &&
    grant.role === "manager" &&
    !grants.some((g) => g.role === "manager" && g.subject !== grant.subject) &&
    !inheritedGrants.some((g) => g.role === "manager");

  const removeAccess = async (grant: SpaceGrant) => {
    const user = grant.subject.replace(/^user:/, "");
    try {
      await revokeSpaceRole(space.space_id, user, grant.role);
      showNotificationGlobal(
        `${grantLabel(grant)} no longer has access to "${space.name}"`,
        "success",
      );
      // Revoking your OWN grant can take the space off your rail entirely —
      // unless you govern the org, which keeps it visible. The server decides,
      // so re-read rather than predict, and leave a page you can no longer see.
      if (user === selfId) {
        await refreshSpaces();
        const stillVisible = useOrgContextStore
          .getState()
          .spaces.some((s) => s.space_id === space.space_id);
        if (!stillVisible) {
          navigate("/overview");
          return;
        }
      }
      reloadGrants();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Revoke failed"), "error");
    }
  };

  const loadAnalysis = async () => {
    setAnalysis(null);
    setAnalysisError(null);
    try {
      setAnalysis(await getSpaceDeletionAnalysis(space.space_id));
    } catch (error) {
      setAnalysisError(doorMessage(error, "Could not analyse the space"));
    }
  };

  const openDelete = () => {
    setTypedName("");
    setDeleteOpen(true);
    void loadAnalysis();
  };

  const confirmDelete = async () => {
    if (!analysis) return;
    try {
      const { purge_at, spaces_scheduled } = await deleteSpace(
        space.space_id,
        analysis.confirm_token,
      );
      const subSpaces = Math.max(spaces_scheduled.length - 1, 0);
      showNotificationGlobal(
        subSpaces > 0
          ? `"${space.name}" and ${subSpaces} ${plural(subSpaces, "sub-space")} are in the Trash until ${formatDayMonthYear(purge_at)}.`
          : `"${space.name}" is in the Trash until ${formatDayMonthYear(purge_at)}.`,
        "success",
      );
      setDeleteOpen(false);
      // Deliberately staying: the space is still live, and this page is where
      // the countdown and the cancel live.
      await refreshSpaces();
    } catch (error) {
      // A stale token (the space changed under us) comes back as a refusal;
      // re-read so the dialog shows the current picture before a retry.
      showNotificationGlobal(doorMessage(error, "Delete failed"), "error");
      void loadAnalysis();
    }
  };

  const undoDeletion = async () => {
    try {
      await cancelSpaceDeletion(space.space_id);
      showNotificationGlobal(
        `Deletion of "${space.name}" cancelled`,
        "success",
      );
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(
        doorMessage(error, "Could not cancel the deletion"),
        "error",
      );
    }
  };

  // D28: managers seal empty spaces; org admins un-seal (in a non-personal org, a
  // successful audit fetch is the admin signal). The server enforces both.
  const canUnseal = (org?.is_personal ?? false) || audit.available === true;
  const canSeal = !space.sealed && looksEmpty;
  const confirmSeal = async () => {
    try {
      await sealSpace(space.space_id);
      showNotificationGlobal(`Space "${space.name}" sealed`, "success");
      setSealOpen(false);
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Seal failed"), "error");
    }
  };
  const confirmUnseal = async () => {
    try {
      await unsealSpace(space.space_id);
      showNotificationGlobal(`Space "${space.name}" unsealed`, "success");
      setUnsealOpen(false);
      await refreshSpaces();
    } catch (error) {
      showNotificationGlobal(doorMessage(error, "Unseal failed"), "error");
    }
  };

  // The door refuses a grant on an Open space (D50): the controls point up the tree.
  const inheritsAccess = space.classification === "open";

  const directUserGrants = grants.filter((g) => g.subject.startsWith("user:"));
  // Listed read-only so no grant that still confers access goes unseen.
  const nonUserGrants = grants.filter((g) => !g.subject.startsWith("user:"));

  return (
    <>
      <SettingsFrame
        crumbs={[
          {
            icon: org && <OrgMark org={org} size="sm" />,
            label: org?.name,
            onClick: goTo(null),
          },
          ...(parent
            ? [
                {
                  icon: <SpaceDot color={spaceColor(parent)} />,
                  label: parent.name,
                  onClick: goTo(parent.space_id),
                },
              ]
            : []),
          {
            icon: <SpaceDot color={spaceColor(space)} />,
            label: space.name,
            onClick: goTo(space.space_id),
          },
        ]}
        actions={
          governsSpace &&
          !space.deletion_scheduled_at && (
            <TooltipButton
              title="New space"
              aria-label={`New space inside ${space.name}`}
              variant="default"
              size="xs"
              onClick={() => setCreateInsideOpen(true)}
            >
              <AddIcon fontSize="inherit" />
            </TooltipButton>
          )
        }
        closeLabel="Close space settings"
        onClose={() => navigate("/overview")}
        navLabel="Space settings sections"
        sections={sections}
        section={section}
        onSection={setSection}
      >
        {section === "general" && (
          <>
            <section className="flex flex-col">
              <SectionRule label="Identity" />
              <div className="flex min-h-12 items-center gap-3 px-2 py-2">
                <SpaceColorPicker
                  color={spaceColor(space)}
                  onPick={(color) => void pickColor(color)}
                />
                <span className="min-w-0 text-fg-title">
                  <EditableName
                    name={space.name}
                    isEditing={isEditingName}
                    canEdit
                    editTrigger="click"
                    ariaLabel="Space name"
                    maxLength={MAX_SPACE_NAME_CHARS}
                    editColor={resolveDatasetColor(spaceColor(space))}
                    setIsEditing={setIsEditingName}
                    update={() => {}}
                    commit={commitName}
                    abort={() => setIsEditingName(false)}
                  />
                </span>
                <Badge
                  className="shrink-0"
                  label={
                    space.sealed ? (
                      <span className="flex items-center gap-1">
                        <LockOutlinedIcon sx={{ fontSize: 10 }} />
                        Sealed
                      </span>
                    ) : (
                      roleLabel(space.classification)
                    )
                  }
                />
              </div>
              {org && (
                <div className="flex min-h-8 items-center gap-2 px-2">
                  <span className="w-33 shrink-0 text-fg-muted-medium">
                    Organization
                  </span>
                  <span className="flex min-w-0 items-center gap-2 text-fg-regular">
                    <OrgMark org={org} size="sm" />
                    <span className="truncate">{org.name}</span>
                  </span>
                </div>
              )}
              <div className="flex min-h-8 items-start gap-2 px-2 py-1.5">
                <span className="w-33 shrink-0 text-fg-muted-medium">
                  Access inheritance
                </span>
                <span className="text-fg-regular">
                  {space.classification !== "open" ? (
                    "Nothing is inherited: everyone with access was added here."
                  ) : parent ? (
                    <>
                      Everyone in{" "}
                      <span className="text-fg-medium">{parent.name}</span> can
                      open it
                      {/* D39: what a closed ancestor imposes, which "open" alone would hide. */}
                      {parent.classification === "sealed"
                        ? ", and as there, nothing leaves and only its people can see it."
                        : parent.classification === "restricted"
                          ? ", and as there, only its people can see it."
                          : "."}
                    </>
                  ) : (
                    "Top level: nothing is inherited."
                  )}
                </span>
              </div>
            </section>

            {governsSpace && (
              // Org settings' Danger: each act a box that warms to red, no rule.
              <section aria-label="Danger" className="flex flex-col gap-2">
                {space.deletion_scheduled_at ? (
                  <Callout
                    variant="warning"
                    title={
                      space.deletion_started_at
                        ? "Being deleted"
                        : `In the Trash until ${purgeDate(space.deletion_scheduled_at)}`
                    }
                  >
                    <span className="flex flex-col items-start gap-2">
                      {space.deletion_started_at
                        ? "The teardown is running now. Everything inside this space is on its way out, and it can no longer be stopped."
                        : "Its sub-spaces and models went with it. Managers restore it from the Trash, or cancel here."}
                      {!space.deletion_started_at && (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => void undoDeletion()}
                        >
                          Cancel deletion
                        </Button>
                      )}
                    </span>
                  </Callout>
                ) : (
                  <>
                    {canSeal && (
                      <DangerLink
                        label="Seal space"
                        onClick={() => setSealOpen(true)}
                      />
                    )}
                    {space.sealed && canUnseal && (
                      <DangerLink
                        label="Unseal space"
                        onClick={() => setUnsealOpen(true)}
                      />
                    )}
                    <DangerLink label="Delete space" onClick={openDelete} />
                  </>
                )}
              </section>
            )}
          </>
        )}

        {section === "access" && (
          <section className="flex flex-col">
            <SectionRule
              label="Access"
              trailing={`${grants.length} direct${
                inheritedGrants.length > 0
                  ? ` · ${inheritedGrants.length} inherited`
                  : ""
              }`}
            />
            <div className="flex flex-col gap-2">
              {governsSpace && isAbandoned(space) && (
                <Callout variant="warning" title="This space has no manager">
                  <span className="flex flex-col items-start gap-2">
                    Whoever managed it is gone. Nobody owns the models inside it
                    or can grant access to it until you appoint someone.
                    {inheritsAccess && granting ? (
                      <MetaAddButton
                        label={`Appoint one on ${granting.name}`}
                        icon={<PersonAddAltOutlinedIcon className="icon-xs" />}
                        onClick={() =>
                          navigate(`/spaces/${granting.space_id}#access`)
                        }
                      />
                    ) : (
                      <MetaAddButton
                        label="Assign manager"
                        icon={<PersonAddAltOutlinedIcon className="icon-xs" />}
                        onClick={() => setAssignOpen(true)}
                      />
                    )}
                  </span>
                </Callout>
              )}
              {governsSpace && inheritsAccess && granting && (
                <Callout variant="info">
                  <span className="flex flex-col items-start gap-2">
                    Access comes from {granting.name}: everyone there can open
                    this space, and nobody can be added here. For a separate
                    group of people, use a top-level space.
                    <MetaAddButton
                      label={`${MANAGE_ACCESS_MENU_LABEL} on ${granting.name}`}
                      icon={<CATALOG.manageAccess.Icon className="icon-xs" />}
                      onClick={() =>
                        navigate(`/spaces/${granting.space_id}#access`)
                      }
                    />
                  </span>
                </Callout>
              )}
              {governsSpace && !inheritsAccess && (
                <ShareComposer
                  chips={grantChips}
                  onChipsChange={setGrantChips}
                  chipInput={{
                    directory,
                    directoryLabel: org?.name,
                    alreadyGranted: grantedEmails,
                  }}
                  roles={SPACE_ROLES}
                  role={roleLabel(grantRole)}
                  onRole={(role) => setGrantRole(role.toLowerCase())}
                  onShare={() => void submitGrant()}
                  sharing={isGranting}
                  shared={granted}
                  error={grantError}
                  onErrorChange={setGrantError}
                  sharedMessage={`Shared ${space.name}`}
                  onEdit={() => setGranted(false)}
                />
              )}
              {grants.length > 0 && (
                <PeopleScroll fill className="mt-2">
                  <div className="space-y-1">
                    {directUserGrants.map((grant) => (
                      <GrantRow
                        key={`${grant.subject}-${grant.role}`}
                        grant={grant}
                        orgRole={
                          grant.email
                            ? orgRoleByEmail.get(grant.email)
                            : undefined
                        }
                        canManage={governsSpace}
                        canRemove={
                          !(
                            org?.is_personal &&
                            grant.subject === `user:${selfId}`
                          )
                        }
                        cannotStepDown={isYourOnlyManagerGrant(grant)}
                        onChangeRole={(role) => void changeRole(grant, role)}
                        onRemove={() =>
                          strandsTheSpace(grant)
                            ? setRevoking(grant)
                            : void removeAccess(grant)
                        }
                      />
                    ))}
                    {nonUserGrants.map((grant) => (
                      <div
                        key={`${grant.subject}-${grant.role}`}
                        className="flex h-8 items-center gap-2 rounded-app px-2"
                      >
                        <span className="min-w-0 flex-1 truncate text-fg-regular">
                          {grant.subject}
                        </span>
                        <span className="text-fg-muted-regular-small">
                          {roleLabel(grant.role)}
                        </span>
                      </div>
                    ))}
                  </div>
                </PeopleScroll>
              )}
              {inheritedGrants.length > 0 && granting && (
                <div className="flex flex-col pt-2">
                  <SpaceBlock
                    name={granting.name}
                    color={spaceColor(granting)}
                    caption="Inherited access"
                    open
                    count={inheritedGrants.length}
                  >
                    <PeopleScroll fill>
                      <div className="space-y-1">
                        {inheritedGrants.map((grant) => (
                          <AccessItem
                            key={`${grant.subject}-${grant.role}`}
                            primaryText={grantLabel(grant)}
                            email={
                              grant.name && grant.email
                                ? grant.email
                                : undefined
                            }
                            isCurrentUser={grant.subject === `user:${selfId}`}
                            permissionLabel={
                              <StaticRole
                                label={roleLabel(grant.role)}
                                tone="medium"
                              />
                            }
                          />
                        ))}
                      </div>
                    </PeopleScroll>
                  </SpaceBlock>
                </div>
              )}
            </div>
          </section>
        )}

        {section === "governance" && (
          <section className="flex flex-col">
            <SectionRule
              label="Governance"
              info="Org admins govern this space but can't read it: they can rename, delete and appoint managers. Reading takes a grant, which shows in Access like any other."
              action={
                audit.entries.length > 0
                  ? {
                      label: "Show all",
                      onClick: () => setAuditPanelOpen(true),
                    }
                  : undefined
              }
            />
            <GovernanceAuditList entries={audit.entries} names={audit.names} />
          </section>
        )}
      </SettingsFrame>

      <Dialog
        open={sealOpen}
        title={`Seal "${space.name}"?`}
        onClose={() => setSealOpen(false)}
      >
        <span className="text-fg-regular">
          Only the people you add to this space will see it, and nothing
          inherits in. Its data stays inside: it can't be exported, copied,
          imported elsewhere or shared one model at a time.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setSealOpen(false),
            },
            {
              label: "Seal space",
              variant: "primary",
              onClick: () => void confirmSeal(),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={unsealOpen}
        title={`Unseal "${space.name}"?`}
        onClose={() => setUnsealOpen(false)}
      >
        <span className="text-fg-regular">
          Its data can be exported, copied and shared again. Sub-spaces sealed
          only because this one was are unsealed with it; any sealed on their
          own stay sealed. Nobody gains or loses access, and the change is
          recorded in the governance log.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setUnsealOpen(false),
            },
            {
              label: "Unseal space",
              variant: "primary",
              onClick: () => void confirmUnseal(),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={deleteOpen}
        title={`Delete "${space.name}"?`}
        onClose={() => setDeleteOpen(false)}
      >
        {(() => {
          if (analysisError) {
            return (
              <span
                className="text-error"
                data-testid="deletion-analysis-error"
              >
                {analysisError}
              </span>
            );
          }
          if (!analysis) {
            return (
              <span
                className="text-fg-muted-regular"
                data-testid="deletion-analysis-loading"
              >
                Checking what this deletes…
              </span>
            );
          }
          const nonEmpty =
            analysis.models.length > 0 ||
            analysis.children.length > 0 ||
            analysis.people_losing_access > 0 ||
            guestCount > 0;
          return (
            <Stack gap="xl">
              <Stack gap="md">
                <span className="text-fg-regular">
                  Goes to the Trash for {GRACE_DAYS} days, where any manager can
                  restore it and everything in it.
                </span>
                {analysis.models.length > 0 && (
                  <ImpactRow
                    quiet
                    icon={<DashboardOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.models.length}
                    label={plural(analysis.models.length, "model")}
                  />
                )}
                {analysis.total_tables > 0 && (
                  <ImpactRow
                    quiet
                    icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.total_tables}
                    label={plural(analysis.total_tables, "table")}
                  />
                )}
                {analysis.critical_tables > 0 && (
                  <ImpactRow
                    quiet
                    tone="warning"
                    icon={<TableChartOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.critical_tables}
                    label={`${plural(analysis.critical_tables, "table")} imported elsewhere`}
                    hint="Models outside this space lose their source."
                  />
                )}
                {analysis.children.length > 0 && (
                  <ImpactRow
                    quiet
                    icon={<LayersOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.children.length}
                    label={plural(analysis.children.length, "sub-space")}
                  >
                    <ImpactChips
                      items={analysis.children.map((child) => ({
                        key: child.space_id,
                        label: (
                          <>
                            <SpaceDot
                              color={spaceColor(
                                spaces.find(
                                  (s) => s.space_id === child.space_id,
                                ) ?? { space_id: child.space_id, color: "" },
                              )}
                            />
                            {child.name}
                          </>
                        ),
                      }))}
                    />
                  </ImpactRow>
                )}
                {analysis.people_losing_access > 0 && (
                  <ImpactRow
                    quiet
                    icon={<PeopleOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={analysis.people_losing_access}
                    label={`${analysis.people_losing_access === 1 ? "person loses" : "people lose"} access`}
                  >
                    <ImpactChips
                      items={losingAccess.map((who) => ({
                        key: who.key,
                        label: (
                          <>
                            {who.label}
                            <span className="text-fg-muted-regular-small">
                              {roleLabel(who.role)}
                            </span>
                          </>
                        ),
                      }))}
                    />
                  </ImpactRow>
                )}
                {guestCount > 0 && (
                  <ImpactRow
                    quiet
                    tone="warning"
                    icon={<PersonOffOutlinedIcon sx={{ fontSize: 16 }} />}
                    count={guestCount}
                    label={`guest ${plural(guestCount, "grant")}`}
                  />
                )}
              </Stack>
              {nonEmpty && (
                <Stack gap="sm">
                  <span className="text-fg-regular">
                    Type <span className="font-medium">{space.name}</span> to
                    confirm.
                  </span>
                  <TextInput
                    value={typedName}
                    onChange={setTypedName}
                    ariaLabel="Type the space name to confirm"
                  />
                </Stack>
              )}
            </Stack>
          );
        })()}
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setDeleteOpen(false),
            },
            {
              label: "Delete space",
              variant: "primary",
              onClick: () => void confirmDelete(),
              disabled:
                !analysis ||
                ((analysis.models.length > 0 ||
                  analysis.children.length > 0 ||
                  analysis.people_losing_access > 0 ||
                  guestCount > 0) &&
                  typedName.trim() !== space.name),
            },
          ]}
        />
      </Dialog>

      <Dialog
        open={revoking !== null}
        title={`Remove ${revoking ? grantLabel(revoking) : ""}'s access?`}
        onClose={() => setRevoking(null)}
      >
        <span className="pb-1 text-fg-muted-regular">
          They are the only manager, so this leaves the space with none:
        </span>
        <div className="flex flex-col gap-3 pt-1">
          <ImpactRow
            tone="warning"
            icon={<PersonOffOutlinedIcon sx={{ fontSize: 16 }} />}
            label="Nobody owns the models inside it"
            hint="Until an org owner or admin appoints a manager."
          />
        </div>
        <span className="text-fg-muted-regular">
          You can grant someone else manager first, or appoint one afterwards
          from this page — an org governor never loses that right.
        </span>
        <DialogActions
          actions={[
            {
              label: "Cancel",
              variant: "secondary",
              onClick: () => setRevoking(null),
            },
            {
              label: "Remove anyway",
              variant: "primary",
              onClick: () => {
                const grant = revoking;
                setRevoking(null);
                if (grant) void removeAccess(grant);
              },
            },
          ]}
        />
      </Dialog>

      <NewSpaceOverlay
        open={createInsideOpen}
        onClose={() => setCreateInsideOpen(false)}
        parentId={space.space_id}
      />

      <AssignManagerDialog
        open={assignOpen}
        space={space}
        members={members}
        onClose={() => setAssignOpen(false)}
        onAssigned={() => {
          void refreshSpaces();
          reloadGrants();
        }}
      />

      <GovernanceAuditPanel
        open={auditPanelOpen}
        onClose={() => setAuditPanelOpen(false)}
        orgId={org && !org.is_personal ? org.org_id : null}
        spaceId={space_id}
        title={`Governance · ${space.name}`}
      />
    </>
  );
};

const GrantRow: React.FC<{
  grant: SpaceGrant;
  /// `owner` / `org_admin` when the grantee governs this space through the org
  /// (D3) whatever their space role says.
  orgRole: string | undefined;
  canManage: boolean;
  /// A personal org's owner can't revoke their own grant: nobody else could
  /// reappoint them (D49 allows stranding only because it stays recoverable).
  canRemove: boolean;
  /// Your own grant, and the only manager the space has (D66).
  cannotStepDown: boolean;
  onChangeRole: (role: string) => void;
  onRemove: () => void;
}> = ({
  grant,
  orgRole,
  canManage,
  canRemove,
  cannotStepDown,
  onChangeRole,
  onRemove,
}) => {
  const [active, setActive] = useState(false);
  const who = grantLabel(grant);
  const role = roleLabel(grant.role);
  const badges = (grant.is_org_member === false || orgRole) && (
    <>
      {grant.is_org_member === false && <Badge label="Guest" />}
      {orgRole && (
        <Tooltip
          title={`${
            orgRole === "owner" ? "Organization owner" : "Organization admin"
          }\nGoverns every space in the org: can grant, revoke and delete here whatever the role beside this says.`}
        >
          <Badge label={orgRole === "owner" ? "Owner" : "Org admin"} />
        </Tooltip>
      )}
    </>
  );
  return (
    <AccessItem
      primaryText={who}
      isCurrentUser={
        grant.subject === `user:${localStorage.getItem("clientID")}`
      }
      email={grant.name && grant.email ? grant.email : undefined}
      isActive={active}
      tags={badges}
      permissionLabel={
        canManage ? (
          <RowMenu
            label={role}
            ariaLabel={`${role}, access for ${who}`}
            menuLabel={`Access for ${who}`}
            roles={SPACE_ROLES.map((option) => ({
              ...option,
              locked:
                cannotStepDown && option.label !== "Manager"
                  ? "Add another manager before you change your role"
                  : undefined,
            }))}
            current={role}
            onPick={(picked) => onChangeRole(picked.toLowerCase())}
            actions={
              canRemove
                ? [
                    {
                      Icon: PersonRemoveOutlinedIcon,
                      label: "Revoke Access",
                      onClick: onRemove,
                      destructive: true,
                      locked: cannotStepDown
                        ? "Add another manager before you revoke your access"
                        : undefined,
                    },
                  ]
                : []
            }
            onMenuToggle={setActive}
          />
        ) : (
          <StaticRole label={role} tone="medium" />
        )
      }
    />
  );
};
